import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Trash2, Edit3, X, Plus, TrendingUp, TrendingDown, Wallet } from "lucide-react";

const COIN_LIST = [
  { id: "bitcoin", name: "Bitcoin", ticker: "BTC", symbol: "BTCUSDT" },
  { id: "ethereum", name: "Ethereum", ticker: "ETH", symbol: "ETHUSDT" },
  { id: "bnb", name: "BNB", ticker: "BNB", symbol: "BNBUSDT" },
  { id: "xrp", name: "XRP", ticker: "XRP", symbol: "XRPUSDT" },
  { id: "solana", name: "Solana", ticker: "SOL", symbol: "SOLUSDT" },
  { id: "cardano", name: "Cardano", ticker: "ADA", symbol: "ADAUSDT" },
  { id: "dogecoin", name: "Dogecoin", ticker: "DOGE", symbol: "DOGEUSDT" },
  { id: "tron", name: "TRON", ticker: "TRX", symbol: "TRXUSDT" },
  { id: "avalanche", name: "Avalanche", ticker: "AVAX", symbol: "AVAXUSDT" },
  { id: "polkadot", name: "Polkadot", ticker: "DOT", symbol: "DOTUSDT" },
  { id: "chainlink", name: "Chainlink", ticker: "LINK", symbol: "LINKUSDT" },
  { id: "litecoin", name: "Litecoin", ticker: "LTC", symbol: "LTCUSDT" },
  { id: "polygon", name: "Polygon", ticker: "POL", symbol: "POLUSDT" },
  { id: "uniswap", name: "Uniswap", ticker: "UNI", symbol: "UNIUSDT" },
  { id: "near", name: "NEAR Protocol", ticker: "NEAR", symbol: "NEARUSDT" },
  { id: "atom", name: "Cosmos", ticker: "ATOM", symbol: "ATOMUSDT" },
  { id: "arbitrum", name: "Arbitrum", ticker: "ARB", symbol: "ARBUSDT" },
  { id: "optimism", name: "Optimism", ticker: "OP", symbol: "OPUSDT" },
  { id: "injective", name: "Injective", ticker: "INJ", symbol: "INJUSDT" },
  { id: "sui", name: "Sui", ticker: "SUI", symbol: "SUIUSDT" },
  { id: "aptos", name: "Aptos", ticker: "APT", symbol: "APTUSDT" },
  { id: "hyperliquid", name: "Hyperliquid", ticker: "HYPE", symbol: "HYPEUSDT" },
  { id: "pepe", name: "Pepe", ticker: "PEPE", symbol: "PEPEUSDT" },
  { id: "ton", name: "Toncoin", ticker: "TON", symbol: "TONUSDT" },
  { id: "shiba-inu", name: "Shiba Inu", ticker: "SHIB", symbol: "SHIBUSDT" },
  { id: "hedera", name: "Hedera", ticker: "HBAR", symbol: "HBARUSDT" },
  { id: "stellar", name: "Stellar", ticker: "XLM", symbol: "XLMUSDT" },
  { id: "aave", name: "Aave", ticker: "AAVE", symbol: "AAVEUSDT" },
  { id: "render", name: "Render", ticker: "RENDER", symbol: "RENDERUSDT" },
  { id: "the-graph", name: "The Graph", ticker: "GRT", symbol: "GRTUSDT" },
];

function formatPrice(p) {
  if (!p) return "$0.00";
  if (p < 0.01) return "$" + p.toFixed(6);
  if (p < 1) return "$" + p.toFixed(4);
  return "$" + p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatValue(v) {
  if (!v && v !== 0) return "$0.00";
  return "$" + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Portfolio() {
  const [holdings, setHoldings] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ coin_id: "", amount: "", buy_price: "" });
  const [livePriceForForm, setLivePriceForForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const wsRef = useRef(null);

  const loadHoldings = async () => {
    try {
      const { data } = await api.get("/portfolio");
      setHoldings(data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { loadHoldings(); }, []);

  // Fetch live prices for all held coins
  useEffect(() => {
    if (holdings.length === 0) return;
    const symbols = [...new Set(holdings.map((h) => {
      const coin = COIN_LIST.find((c) => c.id === h.coin_id);
      return coin ? coin.symbol.toLowerCase() : null;
    }).filter(Boolean))];

    if (symbols.length === 0) return;

    // Initial REST fetch
    fetch("https://api.binance.com/api/v3/ticker/24hr")
      .then((r) => r.json())
      .then((data) => {
        const priceMap = {};
        data.forEach((d) => {
          priceMap[d.symbol] = {
            price: parseFloat(d.lastPrice),
            change: parseFloat(d.priceChangePercent),
          };
        });
        setPrices(priceMap);
      })
      .catch(() => {});

    // WebSocket for live updates
    const streams = symbols.map((s) => s + "@ticker").join("/");
    const ws = new WebSocket("wss://stream.binance.com:9443/stream?streams=" + streams);
    ws.onmessage = (e) => {
      const { data: d } = JSON.parse(e.data);
      setPrices((prev) => ({
        ...prev,
        [d.s]: { price: parseFloat(d.c), change: parseFloat(d.P) },
      }));
    };
    wsRef.current = ws;
    return () => ws.close();
  }, [holdings.length]);

  const getPrice = (coinId) => {
    const coin = COIN_LIST.find((c) => c.id === coinId);
    if (!coin) return null;
    return prices[coin.symbol]?.price || null;
  };

  const getChange = (coinId) => {
    const coin = COIN_LIST.find((c) => c.id === coinId);
    if (!coin) return null;
    return prices[coin.symbol]?.change || null;
  };

  // Portfolio totals
  const totalValue = holdings.reduce((sum, h) => {
    const price = getPrice(h.coin_id);
    return sum + (price ? h.amount * price : 0);
  }, 0);

  const totalInvested = holdings.reduce((sum, h) => sum + h.amount * h.buy_price, 0);
  const totalPnl = totalValue - totalInvested;
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;

  const total24hChange = holdings.reduce((sum, h) => {
    const price = getPrice(h.coin_id);
    const change = getChange(h.coin_id);
    if (!price || !change) return sum;
    const prevValue = (h.amount * price) / (1 + change / 100);
    return sum + (h.amount * price - prevValue);
  }, 0);

    const openAdd = async () => {
    setEditing(null);
    const firstCoin = COIN_LIST[0];
    setForm({ coin_id: firstCoin.id, amount: "", buy_price: "" });
    setLivePriceForForm(null);
    setShowForm(true);
    // Fetch live price directly
    try {
      const r = await fetch("https://api.binance.com/api/v3/ticker/price?symbol=" + firstCoin.symbol);
      const d = await r.json();
      const price = parseFloat(d.price);
      setLivePriceForForm(price);
      setForm((prev) => ({ ...prev, buy_price: price.toFixed(2) }));
    } catch (_) {}
  };

  const openEdit = (h) => {
    setEditing(h.id);
    setForm({ coin_id: h.coin_id, amount: h.amount.toString(), buy_price: h.buy_price.toString() });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ coin_id: "", amount: "", buy_price: "" });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.coin_id || !form.amount || !form.buy_price) return toast.error("Fill all fields");
    const coin = COIN_LIST.find((c) => c.id === form.coin_id);
    if (!coin) return;
    setSaving(true);
    try {
      const payload = {
        coin_id: form.coin_id,
        coin_name: coin.name,
        ticker: coin.ticker,
        amount: parseFloat(form.amount),
        buy_price: parseFloat(form.buy_price),
      };
      if (editing) {
        await api.put("/portfolio/" + editing, payload);
        toast.success("Holding updated");
      } else {
        await api.post("/portfolio", payload);
        toast.success("Coin added to portfolio");
      }
      closeForm();
      loadHoldings();
    } catch (_) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this coin from your portfolio?")) return;
    try {
      await api.delete("/portfolio/" + id);
      toast.success("Removed");
      loadHoldings();
    } catch (_) {
      toast.error("Could not remove");
    }
  };

  // Allocation percentages
  const allocations = holdings.map((h) => {
    const price = getPrice(h.coin_id);
    const value = price ? h.amount * price : h.amount * h.buy_price;
    return { ...h, value };
  }).sort((a, b) => b.value - a.value);

  const ALLOC_COLORS = ["#facc15", "#3b82f6", "#8b5cf6", "#22c55e", "#ef4444", "#f97316", "#06b6d4"];

  return (
    <div className="pt-2 pb-20">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="font-mono text-xs text-yellow-400 uppercase tracking-[0.3em]">/portfolio</span>
            <h1 className="font-display font-bold text-4xl md:text-5xl text-white tracking-tighter mt-1">My Portfolio</h1>
            <p className="text-zinc-400 mt-1 text-sm">Track your crypto holdings with live prices.</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition"
          >
            <Plus className="w-4 h-4" /> Add Coin
          </button>
        </div>

        {loading ? (
          <div className="text-center text-zinc-500 font-mono py-20">Loading portfolio...</div>
        ) : holdings.length === 0 ? (
          <div className="bg-[#0a0a0b] border border-white/10 p-16 text-center">
            <Wallet className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-display font-bold text-2xl text-white mb-2">No holdings yet</h2>
            <p className="text-zinc-400 mb-6">Add your first coin to start tracking your portfolio.</p>
            <button
              onClick={openAdd}
              className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-6 py-3 hover:bg-yellow-500 transition"
            >
              <Plus className="inline w-4 h-4 mr-1" /> Add Your First Coin
            </button>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Value", value: formatValue(totalValue), sub: null },
                { label: "Total Invested", value: formatValue(totalInvested), sub: null },
                {
                  label: "Total P&L",
                  value: (totalPnl >= 0 ? "+" : "-") + formatValue(totalPnl),
                  sub: (totalPnl >= 0 ? "+" : "") + totalPnlPct.toFixed(2) + "%",
                  positive: totalPnl >= 0,
                },
                {
                  label: "24h Change",
                  value: (total24hChange >= 0 ? "+" : "-") + formatValue(total24hChange),
                  sub: null,
                  positive: total24hChange >= 0,
                },
              ].map((s) => (
                <div key={s.label} className="bg-[#0a0a0b] border border-white/10 p-5">
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{s.label}</div>
                  <div className={"font-display font-bold text-2xl " + (s.positive === undefined ? "text-white" : s.positive ? "text-green-400" : "text-red-400")}>
                    {s.value}
                  </div>
                  {s.sub && (
                    <div className={"text-xs font-mono mt-1 " + (s.positive ? "text-green-400" : "text-red-400")}>{s.sub}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Allocation */}
            <div className="bg-[#0a0a0b] border border-white/10 p-6 mb-6">
              <h3 className="font-display font-bold text-lg text-white mb-4">Allocation</h3>
              <div className="space-y-3">
                {allocations.map((h, i) => {
                  const pct = totalValue > 0 ? (h.value / totalValue) * 100 : 0;
                  return (
                    <div key={h.id} className="flex items-center gap-3">
                      <div className="w-14 text-xs font-mono text-zinc-400 flex-shrink-0">{h.ticker}</div>
                      <div className="flex-1 bg-zinc-800 h-2 rounded-full">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{ width: pct + "%", background: ALLOC_COLORS[i % ALLOC_COLORS.length] }}
                        />
                      </div>
                      <div className="w-12 text-xs font-mono text-white text-right flex-shrink-0">{pct.toFixed(1)}%</div>
                      <div className="w-20 text-xs font-mono text-zinc-400 text-right flex-shrink-0">{formatValue(h.value)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Holdings Table */}
            <div className="bg-[#0a0a0b] border border-white/10 overflow-hidden">
              <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b border-white/10 text-xs font-mono uppercase tracking-widest text-zinc-500">
                <div className="col-span-3">Coin</div>
                <div className="col-span-2 text-right">Holdings</div>
                <div className="col-span-2 text-right">Avg Buy</div>
                <div className="col-span-2 text-right">Current</div>
                <div className="col-span-2 text-right">P&L</div>
                <div className="col-span-1"></div>
              </div>

              {holdings.map((h) => {
                const price = getPrice(h.coin_id);
                const change = getChange(h.coin_id);
                const currentValue = price ? h.amount * price : null;
                const investedValue = h.amount * h.buy_price;
                const pnl = currentValue !== null ? currentValue - investedValue : null;
                const pnlPct = pnl !== null ? (pnl / investedValue) * 100 : null;
                const isPositive = pnl !== null ? pnl >= 0 : null;

                return (
                  <div key={h.id} className="grid grid-cols-12 px-6 py-4 border-b border-white/5 hover:bg-white/5 transition items-center">
                    <div className="col-span-6 md:col-span-3">
                      <Link to={"/coins/" + h.coin_id} className="group">
                        <div className="font-display font-bold text-white group-hover:text-yellow-400 transition">{h.coin_name}</div>
                        <div className="text-xs font-mono text-zinc-500">{h.ticker}</div>
                      </Link>
                    </div>
                    <div className="col-span-6 md:col-span-2 text-right">
                      <div className="font-mono text-sm text-white">{h.amount} {h.ticker}</div>
                      {currentValue !== null && (
                        <div className="text-xs font-mono text-zinc-500">{formatValue(currentValue)}</div>
                      )}
                    </div>
                    <div className="hidden md:block col-span-2 text-right">
                      <div className="font-mono text-sm text-white">{formatPrice(h.buy_price)}</div>
                    </div>
                    <div className="hidden md:block col-span-2 text-right">
                      <div className="font-mono text-sm text-white">{price ? formatPrice(price) : "—"}</div>
                      {change !== null && (
                        <div className={"text-xs font-mono " + (change >= 0 ? "text-green-400" : "text-red-400")}>
                          {change >= 0 ? "+" : ""}{change.toFixed(2)}%
                        </div>
                      )}
                    </div>
                    <div className="hidden md:block col-span-2 text-right">
                      {pnl !== null ? (
                        <>
                          <div className={"font-mono text-sm font-bold " + (isPositive ? "text-green-400" : "text-red-400")}>
                            {isPositive ? "+" : "-"}{formatValue(pnl)}
                          </div>
                          <div className={"text-xs font-mono " + (isPositive ? "text-green-400" : "text-red-400")}>
                            {isPositive ? "+" : ""}{pnlPct.toFixed(2)}%
                          </div>
                        </>
                      ) : <div className="text-zinc-600 font-mono text-sm">—</div>}
                    </div>
                    <div className="hidden md:flex col-span-1 items-center justify-end gap-2">
                      <button onClick={() => openEdit(h)} className="text-zinc-400 hover:text-yellow-400 transition">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(h.id)} className="text-zinc-400 hover:text-red-400 transition">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4">
            <div className="bg-[#0a0a0b] border border-white/10 w-full max-w-md p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display font-bold text-xl text-white">
                  {editing ? "Edit Holding" : "Add Coin"}
                </h3>
                <button onClick={closeForm} className="text-zinc-400 hover:text-white transition">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={save} className="space-y-5">
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-2">Coin</label>
                  <select
                    value={form.coin_id}
                    onChange={(e) => {
                      const coin = COIN_LIST.find((c) => c.id === e.target.value);
                      const livePrice = coin ? prices[coin.symbol]?.price || null : null;
                      setLivePriceForForm(livePrice);
                      setForm({ ...form, coin_id: e.target.value, buy_price: livePrice ? livePrice.toFixed(2) : "" });
                    }}
                    disabled={!!editing}
                    className="w-full bg-[#050505] border border-white/20 px-4 py-3 text-white focus:outline-none focus:border-yellow-400 disabled:opacity-50"
                  >
                    {COIN_LIST.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.ticker})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-2">Amount</label>
                  <input
                    type="number"
                    step="any"
                    placeholder="e.g. 0.5"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full bg-[#050505] border border-white/20 px-4 py-3 text-white focus:outline-none focus:border-yellow-400 placeholder:text-zinc-600"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 block mb-2">
                    Average Buy Price (USD)
                    {livePriceForForm && (
                      <span className="ml-2 text-yellow-400 normal-case tracking-normal">
                        — Live: {formatPrice(livePriceForForm)}
                      </span>
                    )}
                  </label>
                  <input
                    type="number"
                    step="any"
                    placeholder="e.g. 68000"
                    value={form.buy_price}
                    onChange={async (e) => {
                      const coin = COIN_LIST.find((c) => c.id === e.target.value);
                      setForm({ ...form, coin_id: e.target.value, buy_price: "" });
                      setLivePriceForForm(null);
                      if (coin) {
                        try {
                          const r = await fetch("https://api.binance.com/api/v3/ticker/price?symbol=" + coin.symbol);
                          const d = await r.json();
                          const price = parseFloat(d.price);
                          setLivePriceForForm(price);
                          setForm((prev) => ({ ...prev, coin_id: e.target.value, buy_price: price.toFixed(2) }));
                        } catch (_) {}
                      }
                    }}
                    className="w-full bg-[#050505] border border-white/20 px-4 py-3 text-white focus:outline-none focus:border-yellow-400 placeholder:text-zinc-600"
                  />
                </div>

                {form.coin_id && form.amount && form.buy_price && (
                  <div className="bg-zinc-900 border border-white/10 px-4 py-3 text-sm font-mono text-zinc-400">
                    Total invested: <span className="text-white">{formatValue(parseFloat(form.amount) * parseFloat(form.buy_price))}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition disabled:opacity-50"
                >
                  {saving ? "Saving..." : editing ? "Update Holding" : "Add to Portfolio"}
                </button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}